import React, { useCallback, useState } from 'react'
import { Col, Row, Spinner } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { getExternalLink } from '../../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { SyncStatusView } from '../../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import { useSaveTasksMutation } from '../../../../../store/integrations/integrations'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { useCreateTasksBatchMutation, useDeleteTaskMutation } from '../../../../../store/meetingSupport/meetingSupport'
import { TEST_SESSION_FILTER } from '../model'
import { TestResultDisplay } from '../TestResultDisplay'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { MeetingDropdown } from '../MeetingDropdown'
import { RoomSession } from '../../../../../models/Room'
import { ContactDropdown } from '../../../../MeetingSummary/CRM/ContactDropdown'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { useFormContext } from 'react-hook-form'
import { SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import '../MappingTester.scss'
import { useGetMeQuery } from '../../../../../store/user/user'

export interface TaskMappingTesterProps {
  serviceId: IntegrationServiceId
}

const TEST_TASK_TEXT = 'Zocks_test_task'

export const TaskMappingTester: React.FC<TaskMappingTesterProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const {
    formState: { isDirty, isValid },
    watch,
  } = useFormContext<IntegrationSettings>()
  const contactType = watch('mappings.contact.objectName')

  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)

  const [selectedContactExternalId, setSelectedContactExternalId] = useState<string | null>(null)

  const [rawResult, setRawResult] = useState<unknown>(null)
  const [externalLink, setExternalLink] = useState<string | null>(null)
  const [taskSuccess, setTaskSuccess] = useState(false)
  const [taskError, setTaskError] = useState(false)
  const [testInProgress, setTestInProgress] = useState<boolean>(false)

  const [saveTasks] = useSaveTasksMutation()
  const [createTasksBatch] = useCreateTasksBatchMutation()
  const [deleteTask] = useDeleteTaskMutation()
  const [selectedSession, setSelectedSession] = useState<RoomSession | null>(null)

  const { data: currentUser } = useGetMeQuery({ accountId })

  const onSessionSelected = useCallback((session: RoomSession) => {
    setSelectedSession(session)
  }, [])
  const handleRunTest = useCallback(async () => {
    if (!selectedSession || !selectedContactExternalId || !currentUser) {
      return
    }

    setTestInProgress(true)
    setRawResult(null)
    setExternalLink(null)
    setTaskSuccess(false)
    setTaskError(false)

    const userExternalId = currentUser?.integrations?.find((i) => i.serviceId === serviceId)?.externalId ?? selectedContactExternalId

    try {
      const createdTasks = await createTasksBatch({
        accountId,
        payload: [
          {
            task: `${TEST_TASK_TEXT}_${new Date().toISOString()}`,
            assignedTo: { type: 'user', id: currentUser.id },
            type: 'meeting',
            roomId: selectedSession.roomId,
            sessionId: selectedSession.id,
          },
        ],
      }).unwrap()

      const createdTaskId = createdTasks[0]?.id ?? null

      try {
        const dueDate = new Date()
        dueDate.setDate(dueDate.getDate() + 5)

        const result = await saveTasks({
          accountId,
          connector: serviceId,
          body: {
            saveSyncStatus: false,
            relatedTo: { id: selectedContactExternalId, type: contactType },
            tasks: [
              {
                taskId: createdTaskId,
                task: createdTasks[0]?.task,
                status: 'open',
                priority: 'Medium',
                dueDate: dueDate.toISOString(),
                assignedTo: userExternalId,
                zocksAssignedTo: {
                  type: 'user',
                  id: currentUser.id,
                  user: {
                    id: currentUser.id,
                    username: currentUser.username,
                    firstName: currentUser.firstName,
                    lastName: currentUser.lastName,
                    name: currentUser.name,
                    email: currentUser.email,
                    type: currentUser.type,
                  },
                },
              },
            ],
          },
        }).unwrap()

        setTaskSuccess(true)
        setRawResult(result)
        const syncStatus = (result as { data?: SyncStatusView[] } | undefined)?.data?.[0]
        if (syncStatus) {
          setExternalLink(getExternalLink({ syncStatus, currentUser: currentUser }) || null)
        }
      } catch (err) {
        setTaskError(true)
        setRawResult(err)
      } finally {
        if (createdTaskId) {
          await deleteTask({ accountId, taskId: createdTaskId }).unwrap()
        }
      }
    } catch (err) {
      setTaskError(true)
      setRawResult(err)
    } finally {
      setTestInProgress(false)
    }
  }, [selectedSession, selectedContactExternalId, accountId, serviceId, createTasksBatch, saveTasks, deleteTask, currentUser, contactType])

  const onContactOptionSelected = useCallback((option: SelectOption) => {
    setSelectedContactExternalId(option.id)
  }, [])

  return (
    <>
      <Row className='mb-4 mt-2 align-items-center'>
        <Col>Contact</Col>
        <Col>
          <ContactDropdown
            serviceId={serviceId}
            onContactOptionSelected={onContactOptionSelected}
            initialSelectedOptionExternalId={selectedContactExternalId ?? undefined}
          />
        </Col>
      </Row>
      <Row>
        <Col>Meeting</Col>
        <Col>
          <MeetingDropdown sessionFilter={TEST_SESSION_FILTER} onSessionSelected={onSessionSelected} />
        </Col>
      </Row>

      <div className='border border-dark rounded-3 p-4 mt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-list-task fs-5'></i>
              <h6 className='fw-bold mb-0'>Save Tasks Test</h6>
            </div>
            <p className='text-secondary mb-0 small'>Verify that Tasks are saved to your {integrationName} as configured.</p>
          </div>
          <Tooltip id='task-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleRunTest}
              disabled={!selectedSession || !selectedContactExternalId || isFormUnsaved || testInProgress || !currentUser}
            >
              {testInProgress ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>

        <TestResultDisplay
          isLoading={testInProgress}
          loadingText='Saving Task'
          hasResult={taskSuccess || taskError}
          isSuccess={taskSuccess}
          isError={taskError}
          integrationName={integrationName}
          rawResult={rawResult}
          successContent={
            <>
              <p className='mb-3 text-dark ps-4'>Successfully created and exported the test task to {integrationName}</p>
              <div className='ps-4'>
                {externalLink ? (
                  <a href={externalLink} target='_blank' rel='noreferrer' className='btn btn-outline-dark btn-sm'>
                    View in {integrationName}
                  </a>
                ) : (
                  <button className='btn btn-outline-dark btn-sm' disabled>
                    View in {integrationName}
                  </button>
                )}
              </div>
            </>
          }
          errorContent={<p className='mb-3 text-dark ps-4'>Failed to export the task to {integrationName}. Please verify your integration settings.</p>}
        />
      </div>
    </>
  )
}
